import { useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { logOut, selectError, selectLoginStatus, verifyEmail } from "features/auth/authSlice";
import ConfirmModal from "features/confirmModal/ConfirmModal";
import CenterContainer from "features/containers/CenterContainer";
import { useForm } from "hooks/useForm";
import { Alert, Box, Button, Card, TextField, Typography } from "@mui/material";
import { VerifyEmailRequest } from "api/auth/verifyEmail";

const VerifyEmailForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const loginError = useAppSelector(selectError);
  const isLoading = useAppSelector(selectLoginStatus) === "loading";
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Using form hook for validation
  const {
    handleSubmit,
    handleChange,
    data: verifyData,
    errors,
  } = useForm<VerifyEmailRequest>({
    validations: {
      verificationCode: {
        custom: {
          isValid: (value) => value.length > 0,
          message: "Ange en kod",
        },
      },
    },
    initialValues: {
      verificationCode: "",
    },
    onSubmit: () => {
      dispatch(verifyEmail(verifyData));
    },
  });

  return (
    <CenterContainer>
      <Card elevation={8} sx={{ p: 4, borderRadius: "1rem" }}>
        <Typography variant="h1" fontSize="2rem" textAlign="center" mb={2}>
          Verifiera e-post
        </Typography>

        <Typography className="text-center" mb={2}>
          För att komma åt ditt konto behöver du först verifiera din e-post. Ange koden du fått
          skickad till din e-post.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "grid", gap: 1 }}>
          <TextField
            label="Verifieringskod"
            type="text"
            variant="filled"
            error={!!errors.verificationCode}
            helperText={errors.verificationCode}
            value={verifyData.verificationCode}
            onChange={handleChange("verificationCode")}
          />

          {/* Show server errors if present */}
          {loginError && <Alert severity="error">{loginError.message}</Alert>}

          <div className="d-flex justify-content-center">
            <Button
              variant="contained"
              color="error"
              className="me-2"
              onClick={() => setShowConfirmModal(true)}
            >
              Logga ut
            </Button>

            <Button type="submit" variant="contained" loading={isLoading} size="large">
              Verifiera
            </Button>
          </div>
        </Box>
      </Card>

      <ConfirmModal
        handleClose={() => {
          setShowConfirmModal(false);
        }}
        handleConfirm={() => {
          dispatch(logOut());
        }}
        show={showConfirmModal}
        title={"Logga ut"}
        message={"Är du säker på att du vill logga ut?"}
        confirmLabel={"Logga ut mig"}
        dismissLabel={"Avbryt"}
        isLoading={false}
      />
    </CenterContainer>
  );
};

export default VerifyEmailForm;
